import {DateTime} from "luxon";
import {BASE_MONTHS} from "../../../../helpers/months";
import type {EBirdMyDataSchema} from "ebird-mydata-reader";
import {RECENT_YEARS_COUNT} from "./constants";
import type {SpeciesSightingWeek} from "./types";

export function getSpeciesSightingSummary(observations: EBirdMyDataSchema[], currentYear = DateTime.now().year) {
    const sightingYears = observations
        .map(observation => DateTime.fromISO(observation.date).year)
        .filter(year => Number.isFinite(year));
    const uniqueSightingYears = new Set(sightingYears);
    const uniqueSightingYearList = Array.from(uniqueSightingYears);
    const firstYear = Math.min(...uniqueSightingYearList);
    const lastYear = Math.max(...uniqueSightingYearList);
    const hasSightings = uniqueSightingYears.size > 0;
    const recentYearStart = currentYear - RECENT_YEARS_COUNT + 1;
    const recentYearsSeen = uniqueSightingYearList
        .filter(year => year >= recentYearStart && year <= currentYear)
        .length;

    return {
        observationCount: sightingYears.length,
        yearSpan: hasSightings ? lastYear - firstYear + 1 : 0,
        recentYearsSeen,
        yearsSinceLastSighting: hasSightings ? currentYear - lastYear : 0
    };
}

export function getSpeciesSightingWeeks(observations: EBirdMyDataSchema[]): SpeciesSightingWeek[] {
    const counts = new Map<string, number>();

    for (const observation of observations) {
        const date = DateTime.fromISO(observation.date);
        if (!date.isValid) continue;

        const week = Math.ceil(date.day / 7);
        const key = `${date.month}-${week}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return BASE_MONTHS.flatMap(({value: month}) => Array.from({length: 5}, (_, index) => ({
        month,
        week: index + 1,
        observationCount: counts.get(`${month}-${index + 1}`) ?? 0
    })));
}

export function getSightingIntensity(count: number, maxCount: number) {
    return .3 + (.7 * (Math.log(count + 1) / Math.log(maxCount + 1)));
}
